import type { ComponentProps, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { pageSizes, timing } from "../config";
import { api, type OrderFill, type OrderRecord } from "../lib/api";
import { formatBaseUnits, fromBaseUnits } from "../lib/amount";
import { formatRate, shortHash } from "../lib/format";
import { shortAddress } from "../lib/auth";
import { getExplorerTxUrl } from "../lib/runtimeConfig";
import { decimalsBySymbol, tokenBySymbol } from "../lib/tokens";
import { TxLink } from "./ExplorerLink";
import { TokenIcon } from "./TokenIcon";

/** Number of columns in the table — the expanded fills row spans all of them. */
const COLUMN_COUNT = 7;

interface TradeHistoryProps {
  /** Bump to refresh at once, e.g. after a submit. */
  refreshKey?: number;
}

const placedOnFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

/** "2026-08-12T21:19:00Z" -> "Aug 12, 09:19 PM". */
function formatPlacedOn(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return placedOnFormatter.format(new Date(ms));
}

/** The gateway's `display` legs; a row stored without them shows raw amounts and "?" symbols. */
function viewOf(record: OrderRecord) {
  return (
    record.display ?? {
      sellToken: '?',
      buyToken: '?',
      sellAmount: record.order.makingAmount ?? '0',
      buyAmount: record.order.takingAmount ?? '0',
    }
  );
}

/** "1 EURC = 1.0004 EUR" — price of one purchased unit in sold units. */
function tradePrice(record: OrderRecord): string {
  // Convert each leg with its own token's decimals (IDRX 2, BBRL/BRZ 18, …).
  const sell = Number(
    fromBaseUnits(viewOf(record).sellAmount, decimalsBySymbol(viewOf(record).sellToken)) ?? Number.NaN,
  );
  const buy = Number(
    fromBaseUnits(viewOf(record).buyAmount, decimalsBySymbol(viewOf(record).buyToken)) ?? Number.NaN,
  );
  if (!Number.isFinite(sell) || !Number.isFinite(buy) || buy === 0) return "—";
  return `1 ${viewOf(record).buyToken} = ${formatRate(sell / buy)} ${viewOf(record).sellToken}`;
}

function placedBy(record: OrderRecord): { short: string; full: string } | null {
  const maker = (record.order as { maker?: unknown }).maker;
  if (typeof maker !== "string" || !/^0x[0-9a-fA-F]{40}$/.test(maker)) return null;
  return { short: shortAddress(maker), full: maker };
}

function isFilled(status: string): boolean {
  return ["filled", "executed", "settled"].includes(status.toLowerCase());
}

/** Fills with a transaction to link, newest first; one still being indexed shows "—". */
function linkableFills(record: OrderRecord): (OrderFill & { txHash: string })[] {
  const fills = record.fills;
  if (!Array.isArray(fills)) return [];
  return fills.filter(
    (f): f is OrderFill & { txHash: string } =>
      typeof f?.txHash === "string" && f.txHash !== "",
  );
}

/** Amount with the integer part dark and the decimals lighter. */
function SplitAmount({
  symbol,
  baseAmount,
  tone,
}: {
  symbol: string;
  baseAmount: string;
  tone: "buy" | "buy-filled" | "sold";
}) {
  const formatted = formatBaseUnits(baseAmount, decimalsBySymbol(symbol));
  const dot = formatted.lastIndexOf(".");
  const intPart = dot === -1 ? formatted : formatted.slice(0, dot);
  const decPart = dot === -1 ? "" : formatted.slice(dot);
  return (
    <span className={`row-amount row-amount-${tone}`}>
      <span className="row-amount-symbol">{symbol}</span>{" "}
      <span className="row-amount-int">{intPart}</span>
      <span className="row-amount-dec">{decPart}</span>
    </span>
  );
}

function csvEscape(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/** Opens a settlement on the deployment's block explorer, in a new tab. */
function openSettlement(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Where the newest settlement opens; null when there is none, or no explorer is named. */
function settlementUrl(newest: (OrderFill & { txHash: string }) | undefined): string | null {
  return newest === undefined ? null : getExplorerTxUrl(newest.txHash);
}

/**
 * A settled row is a link (role, focus, Enter and Space), since a bare `onClick`
 * on a `<tr>` is neither discoverable nor keyboard-reachable. A row with nothing
 * to open gets none of it: a dead affordance is worse than none.
 */
function rowLinkProps(
  newest: (OrderFill & { txHash: string }) | undefined,
): ComponentProps<"tr"> {
  const url = settlementUrl(newest);
  if (newest === undefined || url === null) return { className: "history-row" };
  const open = () => openSettlement(url);
  return {
    className: "history-row history-row-link",
    role: "link",
    tabIndex: 0,
    title: `Open settlement ${shortHash(newest.txHash)} on the explorer`,
    onClick: open,
    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      // Space would otherwise scroll the page.
      e.preventDefault();
      open();
    },
  };
}

/**
 * Keeps a click on a nested control (the explorer link, the +N expander) from
 * also firing the row's open-settlement handler.
 */
function stopRowActivation(e: MouseEvent | KeyboardEvent): void {
  e.stopPropagation();
}

export function TradeHistory({ refreshKey = 0 }: TradeHistoryProps) {
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  // Order hashes whose full fill list is expanded (multi-fill orders only).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const toggleExpanded = useCallback((orderHash: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(orderHash)) next.add(orderHash);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await api.listOrders(pageSizes.history);
      setOrders(Array.isArray(list) ? list : []);
      setError(false);
    } catch {
      // Already reported by the api wrapper; keep last known data on screen.
      setError(true);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), timing.historyPollMs);
    return () => clearInterval(timer);
  }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return orders;
    return orders.filter(
      (r) =>
        viewOf(r).buyToken.toLowerCase().includes(q) ||
        viewOf(r).sellToken.toLowerCase().includes(q),
    );
  }, [orders, search]);

  const handleDownload = () => {
    const header = [
      "Purchased",
      "Sold",
      "Trade Price",
      "Placed on",
      "Status",
      "Transaction",
    ];
    const lines = filtered.map((r) =>
      [
        `${viewOf(r).buyToken} ${formatBaseUnits(viewOf(r).buyAmount, decimalsBySymbol(viewOf(r).buyToken))}`,
        `${viewOf(r).sellToken} ${formatBaseUnits(viewOf(r).sellAmount, decimalsBySymbol(viewOf(r).sellToken))}`,
        tradePrice(r),
        formatPlacedOn(r.createdAt),
        r.status,
        // The full hash, for pasting into an explorer; the newest fill wins.
        linkableFills(r)[0]?.txHash ?? "",
      ]
        .map(csvEscape)
        .join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ailefx-trade-history.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="history-section" aria-label="Trade history">
      <div className="history-header">
        <h2 className="history-title">Trade history</h2>
        <div className="history-tools">
          {error && <span className="history-error">Failed to refresh</span>}
          <div className="search-box">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.4" fill="none" />
              <path d="M9.5 9.5L13 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              type="search"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search trades by token"
            />
          </div>
          <button
            type="button"
            className="tool-button tool-button-icon"
            onClick={() => void load()}
            aria-label="Refresh trade history"
            title="Refresh"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 1.5v3h-3"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            type="button"
            className="tool-button tool-button-icon"
            onClick={handleDownload}
            aria-label="Download trade history as CSV"
            title="Download CSV"
          >
            <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
              <path
                d="M8 2v8M8 10l-3.5-3.5M8 10l3.5-3.5M2.5 13.5h11"
                stroke="currentColor"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="history-card">
        {!loaded ? (
          <p className="history-empty">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="history-empty">
            {orders.length === 0
              ? "No trades yet — submit your first intent above"
              : "No trades match your search"}
          </p>
        ) : (
          <div className="table-scroll">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Purchased</th>
                  <th>Sold</th>
                  <th>Trade Price</th>
                  <th>Placed on</th>
                  <th>Placed by</th>
                  <th>Transaction</th>
                  <th aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((record) => {
                  const fills = linkableFills(record);
                  const newest = fills[0];
                  const isExpanded = expanded.has(record.orderHash);
                  const rowProps = rowLinkProps(newest);
                  return (
                    <Fragment key={record.orderHash}>
                      <tr {...rowProps}>
                        <td>
                          <span className="cell-token">
                            <TokenIcon token={tokenBySymbol(viewOf(record).buyToken)} size={26} />
                            <SplitAmount
                              symbol={viewOf(record).buyToken}
                              baseAmount={viewOf(record).buyAmount}
                              tone={isFilled(record.status) ? "buy-filled" : "buy"}
                            />
                          </span>
                        </td>
                        <td>
                          <span className="cell-token">
                            <TokenIcon token={tokenBySymbol(viewOf(record).sellToken)} size={26} />
                            <SplitAmount
                              symbol={viewOf(record).sellToken}
                              baseAmount={viewOf(record).sellAmount}
                              tone="sold"
                            />
                          </span>
                        </td>
                        <td className="cell-price">{tradePrice(record)}</td>
                        <td className="cell-date">{formatPlacedOn(record.createdAt)}</td>
                        <td className="cell-maker">
                          {((): ReactNode => {
                            const maker = placedBy(record);
                            return maker === null ? (
                              <span className="cell-tx-empty">—</span>
                            ) : (
                              <span title={maker.full}>{maker.short}</span>
                            );
                          })()}
                        </td>
                        <td className="cell-tx">
                          {newest === undefined ? (
                            // No settlement indexed (yet) — nothing to link to.
                            <span className="cell-tx-empty">—</span>
                          ) : (
                            // The row is a link too: a click here must open exactly one tab.
                            <span
                              className="cell-tx-inner"
                              onClick={stopRowActivation}
                              onKeyDown={stopRowActivation}
                            >
                              <TxLink txHash={newest.txHash} />
                              {fills.length > 1 && (
                                <button
                                  type="button"
                                  className="tx-more"
                                  onClick={() => toggleExpanded(record.orderHash)}
                                  aria-expanded={isExpanded}
                                  aria-label={`${isExpanded ? "Hide" : "Show"} all ${fills.length} transactions for this order`}
                                >
                                  +{fills.length - 1}
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                        {/* Visual hint that the row itself opens the explorer;
                            absent when there is nothing to open. */}
                        <td className="cell-chevron">
                          {settlementUrl(newest) !== null && (
                            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                              <path
                                d="M5 3l4 4-4 4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </td>
                      </tr>
                      {isExpanded && fills.length > 1 && (
                        <tr className="tx-subrow">
                          <td colSpan={COLUMN_COUNT}>
                            <ul className="tx-fill-list">
                              {fills.map((fill) => (
                                <li
                                  key={`${fill.txHash}-${fill.logIndex ?? 0}`}
                                  className="tx-fill-row"
                                >
                                  <TxLink txHash={fill.txHash} />
                                  <span className="tx-fill-time">
                                    {fill.blockTimestamp
                                      ? formatPlacedOn(fill.blockTimestamp)
                                      : "—"}
                                  </span>
                                  <span className="tx-fill-amount row-amount">
                                    {fill.kind === "cancel"
                                      ? "cancelled"
                                      : `${formatBaseUnits(
                                          fill.filledMakingAmount,
                                          decimalsBySymbol(viewOf(record).sellToken),
                                        )} ${viewOf(record).sellToken}`}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
